import { z } from 'zod';

/**
 * Resolved content for the three LIVE website sections - what the public
 * `/stays/collections/:sectionId`, `/stays/destinations/:sectionId` and
 * `/flights/deals/:sectionId` routes return.
 *
 * Every route is keyed on a section id rather than on a list of hotel codes or
 * routes in the request. A public endpoint that prices whatever it is handed is
 * an open tap on a paid supplier API; keyed this way, a visitor can only ever
 * ask for what an editor already configured.
 *
 * Every field here is either read from TBO (mirror or live) or derived from our
 * own traffic. Nothing is typed by hand, which is the point: there is no
 * discount badge, no guest score and no "was" price, because TBO returns none
 * and a number nobody measured is worse than no number.
 */

const Paise = z.string().regex(/^\d+$/);
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const StayCollectionCardSchema = z.object({
  hotelCode: z.string(),
  name: z.string(),
  /** 1-5; null when TBO did not classify the property. */
  starRating: z.number().int().nullable(),
  /** TBO's category facet ("Resort", "Apartment" ...). */
  propertyType: z.string().nullable(),
  address: z.string().nullable(),
  neighborhoodName: z.string().nullable(),
  cityId: z.string(),
  cityName: z.string(),
  countryName: z.string().nullable(),
  /** Supplier gallery, cover first. Never empty - a property with no photo is not a card. */
  photoUrls: z.array(z.string()).min(1),
  /** Up to four facility labels, supplier wording. */
  amenities: z.array(z.string()).default([]),
  /** TripAdvisor score, only when TBO's own content carries one. */
  tripAdvisorRating: z.number().min(0).max(5).nullable().default(null),
  /** Closest landmark the mirror knows a distance to. */
  landmark: z.object({ name: z.string(), distanceKm: z.number() }).nullable().default(null),
  /**
   * Null until priced, and on a property with no availability for the stay.
   *
   * The same five figures a search result carries, under the same names, so the
   * website prices a landing card with the helpers it prices a result with
   * (`listPerNightPaise`, `fareSplitLabel`). A card and the results page it
   * links to cannot then disagree about what "per night" means.
   */
  price: z
    .object({
      supplierPricePaise: Paise,
      supplierPerNightPaise: Paise.optional(),
      roomRatePerNightPaise: Paise.optional(),
      perNightPricePaise: Paise,
      totalPricePaise: Paise,
      isRefundable: z.boolean(),
    })
    .nullable(),
});
export type StayCollectionCard = z.infer<typeof StayCollectionCardSchema>;

export const StayCollectionResponseSchema = z.object({
  sectionId: z.string(),
  /** The stay every price on the row was quoted for. */
  checkIn: IsoDate,
  checkOut: IsoDate,
  nights: z.number().int().min(1),
  currencyCode: z.string(),
  cards: z.array(StayCollectionCardSchema),
  /**
   * True while a pricing run is in flight and no usable price is cached yet.
   * The row renders its cards straight away and asks again shortly.
   */
  pricesPending: z.boolean(),
  /** When the prices were fetched from TBO; null while none have been. */
  pricedAt: z.string().nullable(),
});
export type StayCollectionResponse = z.infer<typeof StayCollectionResponseSchema>;

export const StayDestinationTileSchema = z.object({
  cityId: z.string(),
  cityName: z.string(),
  countryName: z.string().nullable(),
  countryCode: z.string().nullable(),
  /** Properties in the TBO mirror for this city. */
  hotelCount: z.number().int().min(0),
  /** The editor's uploaded image, when there is one. */
  imageMediaId: z.string().nullable(),
  /** A supplier photo from one of the city's own hotels - the fallback image. */
  photoUrl: z.string().nullable(),
});
export type StayDestinationTile = z.infer<typeof StayDestinationTileSchema>;

export const StayDestinationsResponseSchema = z.object({
  sectionId: z.string(),
  /** Pre-filled stay for the tile links, so a click lands on priced results. */
  checkIn: IsoDate,
  checkOut: IsoDate,
  tiles: z.array(StayDestinationTileSchema),
});
export type StayDestinationsResponse = z.infer<typeof StayDestinationsResponseSchema>;

export const FlightDealCardSchema = z.object({
  from: z.string().length(3),
  to: z.string().length(3),
  fromLabel: z.string(),
  toLabel: z.string(),
  /**
   * The date the card links to: the cheapest date in the window when TBO has a
   * fare, otherwise a date two weeks out so the link still opens a real search.
   */
  date: IsoDate,
  /**
   * Cheapest one-way economy fare TBO has seen for `date`, minor units. Null
   * when the calendar has nothing for the route - the card then offers a live
   * search instead of quoting a number nobody measured.
   */
  pricePaise: Paise.nullable(),
  currencyCode: z.string(),
  /** Both ends are in India. */
  domestic: z.boolean(),
  imageMediaId: z.string().nullable(),
});
export type FlightDealCard = z.infer<typeof FlightDealCardSchema>;

export const FlightDealsResponseSchema = z.object({
  sectionId: z.string(),
  cards: z.array(FlightDealCardSchema),
});
export type FlightDealsResponse = z.infer<typeof FlightDealsResponseSchema>;
